import axios from "axios";
import { getAccessToken } from "./authSession";
import { API_BASE_URL } from "../config";

// Контур «Склад» (собственные склады: склад = отправитель = юрлицо).
// Бэкенд: /developer_panel/own-warehouse/* — см. docs/own_warehouse_development_plan.md.
const BASE = `${API_BASE_URL}/own-warehouse`;

const getAuthHeaders = () => {
    const token = getAccessToken();
    if (!token) {
        window.location.href = "/";
        return {};
    }
    return {
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
    };
};

const getMultipartAuthHeaders = () => {
    const token = getAccessToken();
    if (!token) {
        window.location.href = "/";
        return {};
    }
    return { headers: { Authorization: `Bearer ${token}` } };
};

const withParams = (params = {}) => {
    const clean = {};
    Object.entries(params).forEach(([key, value]) => {
        if (value === undefined || value === null || value === "") return;
        clean[key] = value;
    });
    return { ...getAuthHeaders(), params: clean };
};

// ---- настройки контура (флаги в business_settings, DB-first) ----
export const getOwnWarehouseSettings = async () => (await axios.get(`${BASE}/settings`, getAuthHeaders())).data;
export const updateOwnWarehouseSettings = async (payload) => (await axios.put(`${BASE}/settings`, payload, getAuthHeaders())).data;
export const listOwnWarehouseFulfillmentIssues = async (warehouseId) => (
    await axios.get(`${BASE}/fulfillment/issues`, withParams({ warehouse_id: warehouseId, limit: 500 }))
).data;
export const resolveOwnWarehouseFulfillmentIssue = async (issueId, payload) => (
    await axios.post(`${BASE}/fulfillment/issues/${issueId}/resolve`, payload, getAuthHeaders())
).data;

// ---- реестр складов ----
export const listOwnWarehouses = async () => (await axios.get(`${BASE}/warehouses`, getAuthHeaders())).data;
// 20 с: при обрыве связи запрос не должен висеть бесконечно — мастер покажет ошибку и «Повторить».
export const getOwnWarehousePrerequisites = async (businessOrganizationId) => (
    await axios.get(`${BASE}/warehouses/prerequisites`, {
        ...withParams({ business_organization_id: businessOrganizationId }),
        timeout: 20000,
    })
).data;
export const createOwnWarehouse = async (payload) => (await axios.post(`${BASE}/warehouses`, payload, getAuthHeaders())).data;
export const getOwnWarehouse = async (warehouseId) => (await axios.get(`${BASE}/warehouses/${warehouseId}`, getAuthHeaders())).data;
export const updateOwnWarehouse = async (warehouseId, payload) => (await axios.put(`${BASE}/warehouses/${warehouseId}`, payload, getAuthHeaders())).data;
export const createOwnWarehouseSenderProfile = async (warehouseId, payload) => (
    await axios.post(`${BASE}/warehouses/${warehouseId}/sender-profiles`, payload, getAuthHeaders())
).data;
export const updateOwnWarehouseSenderProfile = async (warehouseId, profileId, payload) => (
    await axios.put(`${BASE}/warehouses/${warehouseId}/sender-profiles/${profileId}`, payload, getAuthHeaders())
).data;
export const getOwnWarehouseSenderProfilesReadiness = async (warehouseId) => (
    await axios.get(`${BASE}/warehouses/${warehouseId}/sender-profiles/readiness`, getAuthHeaders())
).data;
export const getOwnWarehouseNpRegistries = async (warehouseId) => (await axios.get(`${BASE}/warehouses/${warehouseId}/np-registries`, getAuthHeaders())).data;
export const closeOwnWarehouseNpRegistry = async (warehouseId, registryId) => (
    await axios.post(`${BASE}/warehouses/${warehouseId}/np-registries/${registryId}/close`, {}, getAuthHeaders())
).data;
export const openOwnWarehouseNpRegistryPdf = async (warehouseId, registryId) => {
    const response = await axios.get(`${BASE}/warehouses/${warehouseId}/np-registries/${registryId}/print.pdf`, { ...getAuthHeaders(), responseType: "blob" });
    const url = URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
    window.open(url, "_blank", "noopener");
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
};
export const checkOwnWarehouseNpRegistryStatus = async (warehouseId) => (
    await axios.post(`${BASE}/warehouses/${warehouseId}/np-registries/check-status`, {}, getAuthHeaders())
).data;
// ---- остатки, партии, оценка ----
export const getOwnWarehouseStock = async (warehouseId, params = {}) => (
    await axios.get(`${BASE}/warehouses/${warehouseId}/stock`, withParams(params))
).data;
export const downloadOwnWarehouseStock = async (warehouseId, params = {}, filename = "warehouse_stock.xlsx") => {
    const response = await axios.get(`${BASE}/warehouses/${warehouseId}/stock/export.xlsx`, {
        ...withParams(params),
        responseType: "blob",
        timeout: 120000,
    });
    const url = window.URL.createObjectURL(new Blob([response.data], {
        type: response.headers["content-type"] || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }));
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
};
export const getOwnWarehouseSkuBatches = async (warehouseId, sku) => (
    await axios.get(`${BASE}/warehouses/${warehouseId}/stock/${encodeURIComponent(sku)}/batches`, getAuthHeaders())
).data;
export const getOwnWarehouseValuation = async (warehouseId) => (
    await axios.get(`${BASE}/warehouses/${warehouseId}/valuation`, getAuthHeaders())
).data;

// ---- движения (ledger), корректировки, возвраты, резервы ----
export const getOwnWarehouseLedger = async (warehouseId, params = {}) => (
    await axios.get(`${BASE}/warehouses/${warehouseId}/ledger`, withParams(params))
).data;
export const createOwnWarehouseAdjustment = async (warehouseId, payload) => (
    await axios.post(`${BASE}/warehouses/${warehouseId}/adjustments`, payload, getAuthHeaders())
).data;
export const listOwnWarehouseAdjustments = async (warehouseId, params = {}) => (
    await axios.get(`${BASE}/warehouses/${warehouseId}/adjustments`, withParams(params))
).data;
export const listOwnWarehouseWriteoffs = async (warehouseId, params = {}) => (
    await axios.get(`${BASE}/warehouses/${warehouseId}/writeoffs`, withParams(params))
).data;
export const createOwnWarehouseWriteoff = async (warehouseId, payload) => (
    await axios.post(`${BASE}/warehouses/${warehouseId}/writeoffs`, payload, getAuthHeaders())
).data;
export const updateOwnWarehouseWriteoff = async (warehouseId, docId, payload) => (
    await axios.put(`${BASE}/warehouses/${warehouseId}/writeoffs/${docId}`, payload, getAuthHeaders())
).data;
export const deleteOwnWarehouseWriteoff = async (warehouseId, docId) => (
    await axios.delete(`${BASE}/warehouses/${warehouseId}/writeoffs/${docId}`, getAuthHeaders())
).data;
export const previewOwnWarehouseWriteoff = async (warehouseId, docId, payload) => (
    await axios.post(`${BASE}/warehouses/${warehouseId}/writeoffs/${docId}/preview`, payload, getAuthHeaders())
).data;
export const postOwnWarehouseWriteoff = async (warehouseId, docId, payload) => (
    await axios.post(`${BASE}/warehouses/${warehouseId}/writeoffs/${docId}/post`, payload, getAuthHeaders())
).data;
export const cancelOwnWarehouseWriteoff = async (warehouseId, docId) => (
    await axios.post(`${BASE}/warehouses/${warehouseId}/writeoffs/${docId}/cancel`, {}, getAuthHeaders())
).data;
export const lookupOwnWarehouseReturn = async (warehouseId, orderRef) => (
    await axios.get(`${BASE}/warehouses/${warehouseId}/returns/lookup`, withParams({ order_ref: orderRef }))
).data;
export const createOwnWarehouseReturn = async (warehouseId, payload) => (
    await axios.post(`${BASE}/warehouses/${warehouseId}/returns`, payload, getAuthHeaders())
).data;
export const listOwnWarehouseReturns = async (warehouseId, params = {}) => (
    await axios.get(`${BASE}/warehouses/${warehouseId}/returns`, withParams(params))
).data;
export const listOwnWarehouseReservations = async (warehouseId, params = {}) => (
    await axios.get(`${BASE}/warehouses/${warehouseId}/reservations`, withParams(params))
).data;

// ---- накладные: проведение (сами накладные — fulfillmentApi.js) ----
export const listOwnWarehouseInvoices = async (params = {}) => (await axios.get(`${BASE}/invoices`, withParams(params))).data;
export const getOwnWarehouseInvoicePostingPreview = async (invoiceId) => (
    await axios.get(`${BASE}/invoices/${invoiceId}/posting-preview`, getAuthHeaders())
).data;
export const postOwnWarehouseInvoice = async (invoiceId) => (
    await axios.post(`${BASE}/invoices/${invoiceId}/post`, {}, getAuthHeaders())
).data;
export const unpostOwnWarehouseInvoice = async (invoiceId) => (
    await axios.post(`${BASE}/invoices/${invoiceId}/unpost`, {}, getAuthHeaders())
).data;
export const importOwnWarehouseSnimportInvoice = async (warehouseId, { file, supplierId, markupPercent, retailPriceMode }) => {
    const data = new FormData();
    data.append("file", file);
    data.append("supplier_id", String(supplierId));
    if (markupPercent !== "" && markupPercent !== null && markupPercent !== undefined) data.append("markup_percent", String(markupPercent));
    data.append("retail_price_mode", retailPriceMode || "markup_percent");
    return (await axios.post(
        `${BASE}/warehouses/${warehouseId}/invoices/import-snimport`,
        data,
        getMultipartAuthHeaders(),
    )).data;
};

// ---- переоценка ----
export const previewOwnWarehouseRepricing = async (warehouseId, payload) => (
    await axios.post(`${BASE}/warehouses/${warehouseId}/repricing/preview`, payload, getAuthHeaders())
).data;
export const applyOwnWarehouseRepricing = async (warehouseId, payload) => (
    await axios.post(`${BASE}/warehouses/${warehouseId}/repricing/apply`, payload, getAuthHeaders())
).data;
export const listOwnWarehouseRepricingRuns = async (warehouseId, params = {}) => (
    await axios.get(`${BASE}/warehouses/${warehouseId}/repricing/runs`, withParams(params))
).data;

// ---- динамика ----
export const getOwnWarehouseDailyMetrics = async (warehouseId, params = {}) => (
    await axios.get(`${BASE}/warehouses/${warehouseId}/metrics/daily`, withParams(params))
).data;
export const recomputeOwnWarehouseMetrics = async (warehouseId, payload) => (
    await axios.post(`${BASE}/warehouses/${warehouseId}/metrics/recompute`, payload, getAuthHeaders())
).data;
export const getOwnWarehouseTurnover = async (warehouseId, days = 30) => (
    await axios.get(`${BASE}/warehouses/${warehouseId}/turnover`, withParams({ days }))
).data;
export const getOwnWarehouseExpiring = async (warehouseId, days = 90) => (
    await axios.get(`${BASE}/warehouses/${warehouseId}/expiring`, withParams({ days }))
).data;

// ---- офферы (публикация) ----
export const dryRunOwnWarehouseOffers = async (warehouseId) => (
    await axios.post(`${BASE}/warehouses/${warehouseId}/offers/dry-run`, {}, getAuthHeaders())
).data;
export const refreshOwnWarehouseOffers = async (warehouseId) => (
    await axios.post(`${BASE}/warehouses/${warehouseId}/offers/refresh`, {}, getAuthHeaders())
).data;

// ---- документы возвратов по исходной ТТН ----
export const searchOwnWarehouseReturnShipments = async (warehouseId, params) => (
    await axios.get(`${BASE}/warehouses/${warehouseId}/returns/shipments`, withParams(params))
).data;
export const createOwnWarehouseReturnDraft = async (warehouseId, shipmentId) => (
    await axios.post(`${BASE}/warehouses/${warehouseId}/returns/drafts`, { shipment_id: shipmentId }, getAuthHeaders())
).data;
export const getOwnWarehouseReturnDocument = async (warehouseId, id) => (
    await axios.get(`${BASE}/warehouses/${warehouseId}/returns/${id}`, getAuthHeaders())
).data;
export const updateOwnWarehouseReturnDraft = async (warehouseId, id, payload) => (
    await axios.put(`${BASE}/warehouses/${warehouseId}/returns/${id}`, payload, getAuthHeaders())
).data;
export const postOwnWarehouseReturnDocument = async (warehouseId, id) => (
    await axios.post(`${BASE}/warehouses/${warehouseId}/returns/${id}/post`, {}, getAuthHeaders())
).data;
export const deleteOwnWarehouseReturnDraft = async (warehouseId, id) => (
    await axios.delete(`${BASE}/warehouses/${warehouseId}/returns/${id}`, getAuthHeaders())
).data;
export const auditOwnWarehouseReturns = async (warehouseId) => (
    await axios.get(`${BASE}/warehouses/${warehouseId}/returns/audit`, getAuthHeaders())
).data;
